// "First steps" checklist (guided first visit), per account in this browser.
// The account API has no onboarding or preferences field for it yet (only
// style, format, location, occupation, language and retention); see
// GUIDED-PR.md, "Server side" for the field to add. Read and written with the
// onboarding gates' try/catch convenience pattern; without storage it lasts
// for this page load.

export type ChecklistItemId =
  | "setup"
  | "practice"
  | "review"
  | "copy"
  | "extension";
export const CHECKLIST_ITEMS: readonly ChecklistItemId[] = [
  "setup",
  "practice",
  "review",
  "copy",
  "extension",
];
// Installing the Chrome side panel is optional: it never holds the list open.
export const OPTIONAL_ITEMS: ReadonlySet<ChecklistItemId> =
  new Set<ChecklistItemId>(["extension"]);

export interface ChecklistState {
  // Started on a brand-new account's first run; never for older accounts.
  started: boolean;
  done: ChecklistItemId[];
  dismissed: boolean;
}
export const EMPTY_CHECKLIST: ChecklistState = {
  started: false,
  done: [],
  dismissed: false,
};

type Account = string | null | undefined;
const memory = new Map<string, ChecklistState>();
const keyFor = (account: string): string => `guided:checklist:v1:${account}`;

export const readChecklist = (account: Account): ChecklistState => {
  if (!account) return EMPTY_CHECKLIST;
  const key = keyFor(account);
  try {
    const raw = window.localStorage.getItem(key);
    if (raw) {
      const value = JSON.parse(raw) as Partial<ChecklistState>;
      return {
        started: value.started === true,
        done: Array.isArray(value.done)
          ? value.done.filter((id): id is ChecklistItemId =>
              (CHECKLIST_ITEMS as readonly string[]).includes(id),
            )
          : [],
        dismissed: value.dismissed === true,
      };
    }
  } catch {
    // Fall back to this page load.
  }
  return memory.get(key) ?? EMPTY_CHECKLIST;
};

export const writeChecklist = (
  account: Account,
  value: ChecklistState,
): void => {
  if (!account) return;
  const key = keyFor(account);
  memory.set(key, value);
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // A convenience only.
  }
};

export const requiredDone = (state: ChecklistState): boolean =>
  CHECKLIST_ITEMS.filter((id) => !OPTIONAL_ITEMS.has(id)).every((id) =>
    state.done.includes(id),
  );

// Shown (and What's new held back) while started, not dismissed and not
// done.
export const isChecklistOpen = (state: ChecklistState): boolean =>
  state.started && !state.dismissed && !requiredDone(state);

// Tests only.
export const resetChecklistMemory = (): void => {
  memory.clear();
};
